import React, { useEffect, useState, useCallback } from 'react';
import API from '../services/api';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Field, Row, StatusBadge, Notice, inputStyle, selectStyle, apiErrors } from '../components/insurance/formBits';
import { enrollmentBadge, payerSupportSummary } from '../insurance/format';
import { formatLongDate } from '../dateOnly';

// Payers: the insurance companies we bill through Stedi, and which therapist is
// enrolled with each (status + effective date gate claim creation).

const PAYER_BLANK = {
  displayName: '', stediPayerId: '', payerId: '', supportsEligibility: false, supportsClaims: false, supportsClaimStatus: false, supportsEra: false,
  telehealthPosHome: '10', telehealthPosOther: '02', telehealthModifier: '95', timelyFilingDays: 90, notes: '', isActive: true,
};
function payerToForm(p) {
  return {
    displayName: p.display_name, stediPayerId: p.stedi_payer_id, payerId: p.payer_id, supportsEligibility: p.supports_eligibility, supportsClaims: p.supports_claims,
    supportsClaimStatus: p.supports_claim_status, supportsEra: p.supports_era, telehealthPosHome: p.telehealth_pos_home, telehealthPosOther: p.telehealth_pos_other,
    telehealthModifier: p.telehealth_modifier || '', timelyFilingDays: p.timely_filing_days, notes: p.notes || '', isActive: p.is_active,
  };
}
function eraNote(r) {
  return r.eraEnrollmentRequired && r.eraEnrollment
    ? `ERA needs enrollment (${String(r.eraEnrollment.type).toLowerCase().replace('_', '-')}, ${String(r.eraEnrollment.timeframe).toLowerCase()}).`
    : '';
}
// A Stedi lookup result → form values. Only fills; the admin still saves.
function lookupToForm(form, r) {
  return {
    ...form, displayName: r.displayName || form.displayName, stediPayerId: r.stediId || '', payerId: r.primaryPayerId || '',
    supportsEligibility: !!r.supports.eligibility, supportsClaims: !!r.supports.claims, supportsClaimStatus: !!r.supports.claimStatus, supportsEra: !!r.supports.era,
    notes: eraNote(r) || form.notes,
  };
}

export default function AdminPayersPage() {
  const [payers, setPayers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [editing, setEditing] = useState(null);   // null | 'new' | payer id
  const [expanded, setExpanded] = useState(null); // payer id whose enrollments are open
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, e, t] = await Promise.all([API.get('/billing/payers'), API.get('/billing/enrollments'), API.get('/billing/therapists')]);
      setPayers(p.data); setEnrollments(e.data); setTherapists(t.data); setLoadError('');
    } catch (err) { setLoadError(apiErrors(err).message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary-color)' }}>Payers</h1>
          <p style={{ margin: '4px 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>Insurance companies we bill through Stedi, and each therapist's enrollment with them.</p>
        </div>
        <Button onClick={() => setEditing('new')} disabled={editing === 'new'}>Add payer</Button>
      </div>
      {loadError && <Notice tone="bad">{loadError}</Notice>}
      {editing === 'new' && <PayerForm initial={PAYER_BLANK} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      {payers.map((p) => (
        <Card key={p.id} style={{ marginBottom: 12, opacity: p.is_active ? 1 : 0.6 }}>
          {editing === p.id ? (
            <PayerForm initial={payerToForm(p)} payerId={p.id} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--primary-color)' }}>{p.display_name}{!p.is_active && ' (inactive)'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    Payer ID <code>{p.payer_id}</code> · Stedi <code>{p.stedi_payer_id}</code> · timely filing {p.timely_filing_days} days · telehealth POS {p.telehealth_pos_home}/{p.telehealth_pos_other}{p.telehealth_modifier ? ` + ${p.telehealth_modifier}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {payerSupportSummary(p).map((s) => <StatusBadge key={s} tone={s.startsWith('No ') ? 'bad' : 'good'}>{s}</StatusBadge>)}
                  </div>
                  {p.notes && <div style={{ fontSize: 13, marginTop: 8, color: 'var(--text-strong)' }}>{p.notes}</div>}
                </div>
                <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>
                  <div>{p.active_enrollments} active enrollment{p.active_enrollments === 1 ? '' : 's'}</div>
                  <div>{p.active_policies} client polic{p.active_policies === 1 ? 'y' : 'ies'}</div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <Button variant="ghost" style={smallBtn} onClick={() => setEditing(p.id)}>Edit</Button>
                    <Button variant="subtle" style={smallBtn} onClick={() => setExpanded(expanded === p.id ? null : p.id)}>{expanded === p.id ? 'Hide enrollments' : 'Enrollments'}</Button>
                  </div>
                </div>
              </div>
              {expanded === p.id && (
                <EnrollmentsPanel payer={p} rows={enrollments.filter((e) => e.payer_id === p.id)} therapists={therapists} onChanged={load} />
              )}
            </>
          )}
        </Card>
      ))}
      {payers.length === 0 && !loadError && <Card><div style={{ color: 'var(--text-muted)' }}>No payers yet. Use "Add payer" and look the payer up in Stedi.</div></Card>}
    </div>
  );
}

function PayerForm({ initial, payerId, onDone, onCancel }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  async function search(e) {
    e.preventDefault(); setSearching(true); setResults(null);
    try { setResults((await API.get('/billing/payers/search', { params: { q } })).data); }
    catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
    finally { setSearching(false); }
  }
  async function save(e) {
    e.preventDefault();
    try {
      if (payerId) await API.put(`/billing/payers/${payerId}`, form); else await API.post('/billing/payers', form);
      onDone();
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    }
  }

  return (
    <Card style={{ marginBottom: 12, border: '1px solid var(--border-color)' }}>
      <div style={{ fontWeight: 700, color: 'var(--primary-color)', marginBottom: 6 }}>{payerId ? 'Edit payer' : 'New payer'}</div>
      <form onSubmit={search} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Look up in Stedi's payer list" hint="Fills the fields below; nothing is saved until you click Save." style={{ maxWidth: 420 }}>
          <input style={inputStyle} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Carelon" aria-label="Stedi payer search" />
        </Field>
        <Button type="submit" variant="accent" disabled={searching || q.trim().length < 2} style={{ marginBottom: 2 }}>{searching ? 'Searching…' : 'Search'}</Button>
      </form>
      {results && (
        <div style={{ margin: '8px 0', maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          {results.length === 0 && <div style={{ padding: 10, fontSize: 13, color: 'var(--text-muted)' }}>No matches.</div>}
          {results.map((r) => (
            <div key={r.stediId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px', borderBottom: '1px solid #F0EDE8', fontSize: 13, alignItems: 'center' }}>
              <div><strong>{r.displayName}</strong> <span style={{ color: 'var(--text-muted)' }}>· {r.primaryPayerId} · {r.stediId}</span><div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{payerSupportSummary(r).join(' · ')}</div></div>
              <Button variant="subtle" style={smallBtn} type="button" onClick={() => { setForm(lookupToForm(form, r)); setResults(null); }}>Use</Button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={save}>
        <Row>
          <Field label="Display name" error={errors.displayName}><input style={inputStyle} value={form.displayName} onChange={set('displayName')} aria-label="Display name" /></Field>
          <Field label="Payer ID (on claims)" error={errors.payerId} style={{ maxWidth: 180 }}><input style={inputStyle} value={form.payerId} onChange={set('payerId')} aria-label="Payer ID" /></Field>
          <Field label="Stedi payer ID" error={errors.stediPayerId} style={{ maxWidth: 180 }}><input style={inputStyle} value={form.stediPayerId} onChange={set('stediPayerId')} aria-label="Stedi payer ID" /></Field>
        </Row>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, marginTop: 10 }}>
          <label><input type="checkbox" checked={form.supportsEligibility} onChange={set('supportsEligibility')} /> Eligibility</label>
          <label><input type="checkbox" checked={form.supportsClaims} onChange={set('supportsClaims')} /> Claims</label>
          <label><input type="checkbox" checked={form.supportsClaimStatus} onChange={set('supportsClaimStatus')} /> Claim status</label>
          <label><input type="checkbox" checked={form.supportsEra} onChange={set('supportsEra')} /> ERA</label>
          <label><input type="checkbox" checked={form.isActive} onChange={set('isActive')} /> Active</label>
        </div>
        <Row>
          <Field label="Telehealth POS (client at home)" error={errors.telehealthPosHome} style={{ maxWidth: 160 }}><input style={inputStyle} value={form.telehealthPosHome} onChange={set('telehealthPosHome')} aria-label="Telehealth POS home" /></Field>
          <Field label="Telehealth POS (elsewhere)" error={errors.telehealthPosOther} style={{ maxWidth: 160 }}><input style={inputStyle} value={form.telehealthPosOther} onChange={set('telehealthPosOther')} aria-label="Telehealth POS other" /></Field>
          <Field label="Telehealth modifier" error={errors.telehealthModifier} hint="Blank if the payer wants none" style={{ maxWidth: 160 }}><input style={inputStyle} value={form.telehealthModifier} onChange={set('telehealthModifier')} aria-label="Telehealth modifier" /></Field>
          <Field label="Timely filing (days)" error={errors.timelyFilingDays} style={{ maxWidth: 140 }}><input style={inputStyle} type="number" value={form.timelyFilingDays} onChange={set('timelyFilingDays')} aria-label="Timely filing days" /></Field>
        </Row>
        <Field label="Notes" error={errors.notes}><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={set('notes')} aria-label="Notes" /></Field>
        {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Button type="submit">Save</Button>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

const ENROLL_BLANK = { therapistId: '', status: 'pending', effectiveDate: '', terminationDate: '', eraEnrolled: false, notes: '' };

function EnrollmentsPanel({ payer, rows, therapists, onChanged }) {
  const [editingId, setEditingId] = useState(null); // enrollment id | 'new'
  const [draft, setDraft] = useState(ENROLL_BLANK);
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const enrolledIds = new Set(rows.map((r) => r.therapist_id));

  async function save() {
    try {
      // A blank select must reach the server as "missing" (→ inline "Choose a therapist"), not as 0.
      if (editingId === 'new') await API.post(`/billing/payers/${payer.id}/enrollments`, { ...draft, therapistId: draft.therapistId ? Number(draft.therapistId) : undefined });
      else await API.put(`/billing/enrollments/${editingId}`, draft);
      setEditingId(null); setErrors({}); setMsg({ tone: 'good', text: 'Saved.' }); onChanged();
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    }
  }

  const editorCells = (
    <>
      <td style={td}>
        <Field error={errors.status} style={{ minWidth: 120 }}>
          <select style={selectStyle} value={draft.status} onChange={set('status')} aria-label="Enrollment status">
            <option value="pending">Pending</option><option value="active">Active</option><option value="terminated">Terminated</option>
          </select>
        </Field>
      </td>
      <td style={td}><Field error={errors.effectiveDate} style={{ minWidth: 150 }}><input style={inputStyle} type="date" value={draft.effectiveDate || ''} onChange={set('effectiveDate')} aria-label="Effective date" /></Field></td>
      <td style={td}><Field error={errors.terminationDate} style={{ minWidth: 150 }}><input style={inputStyle} type="date" value={draft.terminationDate || ''} onChange={set('terminationDate')} aria-label="Termination date" /></Field></td>
      <td style={td}><label style={{ fontSize: 13 }}><input type="checkbox" checked={draft.eraEnrolled} onChange={set('eraEnrolled')} /> ERA done</label></td>
      <td style={td}><Field error={errors.notes} style={{ minWidth: 160 }}><input style={inputStyle} value={draft.notes || ''} onChange={set('notes')} aria-label="Enrollment notes" /></Field></td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        <Button variant="success" style={smallBtn} onClick={save}>Save</Button>{' '}
        <Button variant="ghost" style={smallBtn} onClick={() => { setEditingId(null); setErrors({}); }}>Cancel</Button>
      </td>
    </>
  );

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary-color)' }}>Enrollments with {payer.display_name}</div>
        <Button variant="subtle" style={smallBtn} onClick={() => { setEditingId('new'); setDraft(ENROLL_BLANK); setErrors({}); setMsg(null); }} disabled={editingId === 'new'}>Add therapist</Button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Claims for a date of service before the effective date are blocked. A blank effective date blocks all claims for that therapist with this payer.</div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead><tr style={{ background: 'var(--secondary-color)' }}>{['Therapist', 'Status', 'Effective', 'Terminated', 'ERA', 'Notes', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {editingId === 'new' && (
              <tr style={{ background: 'var(--surface-muted)' }}>
                <td style={td}>
                  <Field error={errors.therapistId} style={{ minWidth: 170 }}>
                    <select style={selectStyle} value={draft.therapistId} onChange={set('therapistId')} aria-label="Therapist">
                      <option value="">Choose…</option>
                      {therapists.filter((t) => t.is_active && !enrolledIds.has(t.id)).map((t) => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                    </select>
                  </Field>
                </td>
                {editorCells}
              </tr>
            )}
            {rows.map((r) => {
              const b = enrollmentBadge({ status: r.status, effectiveDate: r.effective_date, terminationDate: r.termination_date });
              return editingId === r.id ? (
                <tr key={r.id} style={{ background: 'var(--surface-muted)' }}><td style={td}><strong>{r.therapist_name}</strong></td>{editorCells}</tr>
              ) : (
                <tr key={r.id} style={{ borderBottom: '1px solid #F0EDE8' }}>
                  <td style={td}><strong>{r.therapist_name}</strong></td>
                  <td style={td}><StatusBadge tone={b.tone}>{b.label}</StatusBadge></td>
                  <td style={td}>{r.effective_date ? formatLongDate(r.effective_date) : <span style={{ color: '#B7791F', fontWeight: 600 }}>missing</span>}</td>
                  <td style={td}>{r.termination_date ? formatLongDate(r.termination_date) : '—'}</td>
                  <td style={td}>{r.era_enrolled ? <StatusBadge tone="good">Done</StatusBadge> : <StatusBadge>Not yet</StatusBadge>}</td>
                  <td style={{ ...td, color: 'var(--text-muted)' }}>{r.notes || ''}</td>
                  <td style={td}><Button variant="ghost" style={smallBtn} onClick={() => { setEditingId(r.id); setDraft({ therapistId: r.therapist_id, status: r.status, effectiveDate: r.effective_date || '', terminationDate: r.termination_date || '', eraEnrolled: r.era_enrolled, notes: r.notes || '' }); setErrors({}); setMsg(null); }}>Edit</Button></td>
                </tr>
              );
            })}
            {rows.length === 0 && editingId !== 'new' && <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={7}>No therapists enrolled yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '8px 10px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const smallBtn = { padding: '6px 10px', fontSize: 12 };
